import { Injectable } from '@angular/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { AllPopupModalExecutionServiceService } from 'src/app/shared/services/all-popup-modal-execution-service.service';

const CREDENTIAL_SERVER = 'co.phiedge.in'; // namespace/key for saved credentials
const BIOMETRIC_ENABLED_KEY = 'dwm_plus_biometric_enabled';

@Injectable({
  providedIn: 'root'
})
export class BiometricAuthService {
  constructor(private allPopupModalExecutionServiceService: AllPopupModalExecutionServiceService) {}

  /** Is biometric available on device? */
  async isAvailable(): Promise<boolean> {
    try {
      const res = await NativeBiometric.isAvailable();
      return !!res?.isAvailable;
    } catch (err) {
      // console.warn('Biometric availability check failed', err);
      return false;
    }
  }

  /** Save credentials securely (username/password or token) */
  async saveCredentials(username: string, passwordOrToken: string): Promise<void> {
    await NativeBiometric.setCredentials({
      username,
      password: passwordOrToken,
      server: CREDENTIAL_SERVER
    });
    await Preferences.set({ key: BIOMETRIC_ENABLED_KEY, value: 'true' });
  }

  /** Remove credentials & disable biometric flag */
  async removeCredentials(): Promise<void> {
    try {
      await NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER });
    } catch (err) {
      // ignore if missing
      // console.warn('deleteCredentials error', err);
    }
    await Preferences.remove({ key: BIOMETRIC_ENABLED_KEY });
  }

  /** Is biometric auto-login enabled? */
  async isBiometricEnabled(): Promise<boolean> {
    const p = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    return p.value === 'true';
  }

  /**
   * Verify biometric and return saved credentials if verified.
   * Returns null if verification fails or credentials not available.
   */
  async getVerifiedCredentials(): Promise<{ username: string; password: string } | null> {
    try {
      // first check availability and that we have stored credentials
      const available = await this.isAvailable();
      if (!available) {
        // this.allPopupModalExecutionServiceService.apiSuccessOrErrorMessage({ msg: 'Something went wrong please try later.'});
        return null;
      }

      const have = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
      if (have.value !== 'true') {
        // this.allPopupModalExecutionServiceService.apiSuccessOrErrorMessage({ msg: 'Details not found please enable authentication first.'});
        return null;
      }

      // verify biometric prompt
      await NativeBiometric.verifyIdentity({
        reason: 'Authenticate to log in',
        title: 'Biometric Login',
        subtitle: 'Authenticate to access your account',
        description: ''
      });
      // if (!verify || !verify?.verified) return null;

      // return stored credentials
      const creds = await NativeBiometric.getCredentials({ server: CREDENTIAL_SERVER });
      if (!creds || !creds.username) {
        // this.allPopupModalExecutionServiceService.apiSuccessOrErrorMessage({ msg: 'Something went wrong please try later.'});
        return null;
      }

      return {
        username: creds.username,
        password: creds.password
      };
    } catch (err) {
      // console.warn('getVerifiedCredentials failed', err);
      return null;
    }
  }
}
